import { Backdrop, Box, Container } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { useMemberForm } from '../hooks/use-member-form';

import { BasicInformationSection } from './basic-information-section';
import { DepartmentSection } from './department-section';
import { EmailAddressSection } from './email-address-section';

import { useBeforeUnloadWarning } from '@/hooks/use-beforeunload-warning';
import { useSubmitting } from '@/hooks/use-submitting';
import { NavigationSection } from '@/components/types/types';
import { NavigationPanelCommon } from '@/components/navigation-panel-common';
import { paths } from '@/config/paths';
import HeaderRegister from '@/features/apps/components/header-register';
import FixedFooter from '@/components/footer/fixed-footer';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { PermissionAction, FunctionName, snackbarType } from '@/lib/enums';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { checkPermission } from '@/utils/helpers';

export const MemberForm = ({ canUpdate }: { canUpdate: boolean }) => {
  const {
    formData,
    memberData,
    validationState,
    updateField,
    getFieldError,
    isFieldValid,
    departments,
    isLoadingDepartments,
    isLoading,
    registerMember,
    getMember,
    deleteMember,
    updateMember,
    resetForm,
    hasFormChanges,
    isDeleted,
    restoreMember,
    reinviteMember,
  } = useMemberForm();
  const navigate = useNavigate();
  useBeforeUnloadWarning(hasFormChanges);
  const navigationSections: NavigationSection[] = [
    {
      id: 'basic-information',
      label: '担当者基本情報',
      anchor: 'basic-information-section',
      icon: <PersonOutlineOutlinedIcon />,
    },
    {
      id: 'department',
      label: '部署選択',
      anchor: 'department-section',
      icon: <PersonOutlineOutlinedIcon />,
    },
    {
      id: 'email-address',
      label: 'メールアドレス',
      anchor: 'email-address-section',
      icon: <MailOutlineOutlinedIcon />,
    },
  ];
  const { memberId } = useParams();
  const isEditMode = Boolean(memberId);
  const { setSnackbarMessage } = useSnackbar();
  const { setConfirmDialog } = useConfirmDialog();
  const { setIsLoading } = useLoadingProgress();
  const { setIsSubmitting } = useSubmitting();
  const { permissions } = useOidc();
  const canDelete = useMemo(() => {
    return checkPermission(permissions, FunctionName.STAFF_MANAGEMENT, PermissionAction.DELETE);
  }, [permissions]);

  const canRestore = useMemo(() => {
    return checkPermission(permissions, FunctionName.STAFF_MANAGEMENT, PermissionAction.CREATE);
  }, [permissions]);
  // Fetch member data when in edit mode
  useEffect(() => {
    if (memberId) {
      void getMember(memberId);
    }
  }, [memberId, getMember]);

  const handleCancel = useCallback(() => {
    // Show confirmation dialog
    navigate(paths.members.path);
  }, [resetForm, navigate, setConfirmDialog]);

  const handleRegisterMember = useCallback(async () => {
    setIsLoading(true);
    const { success, message } = await registerMember();
    if (success) {
      setSnackbarMessage(MESSAGES.MEMBER_HAS_BEEN_REGISTERED, { type: snackbarType.SUCCESS });
      setIsSubmitting(true);
      // Redirect to members list after 2 seconds
      setTimeout(() => {
        navigate(paths.members.path);
      }, 2000);
    } else {
      setConfirmDialog({
        title: message,
      });
    }
    setIsLoading(false);
  }, [registerMember]);

  const handleUpdateMember = useCallback(
    async (memberId: string) => {
      setIsLoading(true);
      const success = await updateMember(memberId);
      if (success) {
        setSnackbarMessage(MESSAGES.MEMBER_HAS_BEEN_CHANGED, { type: snackbarType.SUCCESS });
        setIsSubmitting(true);
        // Redirect to members list after 2 seconds
        setTimeout(() => {
          navigate(paths.members.path);
        }, 2000);
      } else {
        setConfirmDialog({
          title: '登録に失敗しました。もう一度お試しください。',
        });
      }
      setIsLoading(false);
    },
    [registerMember]
  );

  const handleFormSubmit = useCallback(async () => {
    if (memberId) {
      await handleUpdateMember(memberId);
    } else {
      await handleRegisterMember();
    }
  }, [handleUpdateMember, handleRegisterMember, memberId]);

  const handleDeleteMember = useCallback(async () => {
    if (!memberId) return;
    setIsLoading(true);
    const { success, message } = await deleteMember(memberId);
    if (success) {
      setSnackbarMessage(MESSAGES.DELETE_STAFF_SUCCESS, { type: snackbarType.SUCCESS });
      setIsSubmitting(true);
      // Redirect to members list after 2 seconds
      setTimeout(() => {
        navigate(paths.members.path);
      }, 2000);
    } else {
      setConfirmDialog({
        title: message,
      });
    }
    setIsLoading(false);
  }, [memberId, deleteMember, setSnackbarMessage, navigate, setConfirmDialog]);

  const handleClickDeleteMember = useCallback(() => {
    setConfirmDialog({
      title: '担当者を削除しますか?',
      confirmDialog: () => {
        void handleDeleteMember();
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  }, [handleDeleteMember, setConfirmDialog]);

  const handleRestoreMember = useCallback(async () => {
    if (!memberId) return;
    const { success, message } = await restoreMember(memberId);
    if (success) {
      setSnackbarMessage(MESSAGES.RESTORE_STAFF_SUCCESS, { type: snackbarType.SUCCESS });
      setIsSubmitting(true);
      setTimeout(() => {
        navigate(paths.members.path);
      }, 2000);
    } else {
      setConfirmDialog({
        title: message,
      });
    }
  }, []);

  const clickRestoreMember = useCallback(async () => {
    setConfirmDialog({
      title: '担当者を復帰しますか?',
      confirmDialog: () => {
        void handleRestoreMember();
      },
      confirmButtonText: '復帰',
      cancelButtonText: 'キャンセル',
    });
  }, [handleRestoreMember]);

  const handleReinviteMember = useCallback(async () => {
    if (!formData.email) return;
    setIsLoading(true);

    const { success, message } = await reinviteMember(formData.email);
    if (success) {
      setSnackbarMessage(MESSAGES.REINVITE_STAFF_SUCCESS, { type: snackbarType.SUCCESS });
    } else {
      setConfirmDialog({
        title: message,
      });
    }
    setIsLoading(false);
  }, [formData.email, setIsLoading, setSnackbarMessage, setConfirmDialog]);

  const renderButtonDelete = useMemo(() => {
    return memberId
      ? [
          isDeleted
            ? {
                label: '復帰',
                onClick: clickRestoreMember,
                variant: 'outlined' as const,
                disabled: isLoading || !canRestore,
                startIcon: <RestoreOutlinedIcon />,
              }
            : {
                label: '削除',
                onClick: handleClickDeleteMember,
                variant: 'outlined' as const,
                startIcon: <DeleteIcon />,
                disabled: isLoading || !canDelete,
                sx: {
                  color: 'red',
                  borderColor: 'red',
                  '&:hover': {
                    borderColor: 'darkred',
                    backgroundColor: 'rgba(255, 0, 0, 0.04)',
                  },
                },
              },
        ]
      : [];
  }, [memberId, isDeleted, isLoading, handleClickDeleteMember, canUpdate]);

  const footerButtons = useMemo(() => {
    const isSubmitDisabled =
      validationState.isSubmitting || !validationState.isValid || (isEditMode && !hasFormChanges); // Disable in edit mode if no changes

    return [
      ...renderButtonDelete,
      {
        label: 'キャンセル',
        onClick: handleCancel,
        variant: 'outlined' as const,
        startIcon: <CancelIcon />,
        disabled: validationState.isSubmitting,
        sx: {
          marginLeft: 'auto',
        },
      },
      {
        label: validationState.isSubmitting ? '登録中...' : '入力内容を決定',
        onClick: handleFormSubmit,
        variant: 'contained' as const,
        startIcon: <SaveIcon />,
        disabled: isSubmitDisabled || !canUpdate,
        loading: validationState.isSubmitting,
      },
      ...(isEditMode
        ? [
            {
              label: '再招待',
              onClick: handleReinviteMember,
              variant: 'outlined' as const,
              startIcon: <MailOutlineOutlinedIcon />,
              disabled: isLoading || !formData.email,
            },
          ]
        : []),
    ];
  }, [
    memberId,
    isLoading,
    validationState,
    handleDeleteMember,
    handleCancel,
    handleFormSubmit,
    handleReinviteMember,
    formData.email,
    renderButtonDelete,
    isEditMode,
    hasFormChanges,
    canUpdate,
  ]);

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister
        title={isEditMode ? '担当者編集フォーム' : '新規担当者登録フォーム'}
        description={isEditMode ? '担当者情報を編集してください' : '担当者情報を入力してください'}
      />

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Form Content */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }} inert={!canUpdate ? true : false}>
          {/* Basic Information Section */}
          <Box id='basic-information-section'>
            <BasicInformationSection
              formData={formData}
              memberData={memberData}
              onUpdateField={updateField}
              isFieldValid={isFieldValid}
              getFieldError={getFieldError}
              isEditMode={isEditMode}
            />
          </Box>

          {/* Department Section */}
          <Box id='department-section'>
            <DepartmentSection
              formData={formData}
              onUpdateField={updateField}
              isFieldValid={isFieldValid}
              getFieldError={getFieldError}
              departmentsLoading={isLoadingDepartments}
              departments={departments}
              isEditMode={isEditMode}
              memberData={memberData}
            />
          </Box>
          {/* Email Address Section */}
          <Box id='email-address-section'>
            <EmailAddressSection
              formData={formData}
              onUpdateField={updateField}
              isFieldValid={isFieldValid}
              getFieldError={getFieldError}
              isEditMode={isEditMode}
            />
          </Box>

          {/* Add bottom padding to prevent content from being hidden behind fixed footer */}
          <Box sx={{ height: '100px' }} />

          <Backdrop
            open={!canUpdate}
            sx={{
              position: 'absolute',
              backgroundColor: 'rgba(0, 0, 0, 0)',
              zIndex: 9999,
            }}
          ></Backdrop>
        </Box>
        {/* Fixed Action Footer */}
        <FixedFooter buttons={footerButtons} isSubmitting={validationState.isSubmitting} />
        {/* Navigation Panel */}
        <Box sx={{ width: 250, flexShrink: 0 }}>
          <NavigationPanelCommon sections={navigationSections} />
        </Box>
      </Box>
    </Container>
  );
};
